import { useState, useEffect } from 'react';

// Filter state interface
export interface StagingFilterState {
  accountName: string;
  sfdcAccountId: string;
  fullName: string;
  email: string;
  activeFilters: string[];
}

// Custom hook for staging filter management
export const useStagingFilter = () => {
  const [filterDrawerOpen, setFilterDrawerOpen] = useState<boolean>(false);
  const [filterState, setFilterState] = useState<StagingFilterState>({
    accountName: '',
    sfdcAccountId: '',
    fullName: '',
    email: '',
    activeFilters: []
  });
  
  const [tempState, setTempState] = useState<Omit<StagingFilterState, 'activeFilters'>>({
    accountName: '',
    sfdcAccountId: '',
    fullName: '',
    email: ''
  });
  
  const [expandedAccordions, setExpandedAccordions] = useState<{[key: string]: boolean}>({
    accountName: true,
    sfdcAccountId: true,
    fullName: true,
    email: true
  });

  const handleAccountNameChange = (value: string) => {
    setTempState(prev => ({ ...prev, accountName: value }));
  };

  const handleSfdcAccountIdChange = (value: string) => {
    setTempState(prev => ({ ...prev, sfdcAccountId: value }));
  };

  const handleFullNameChange = (value: string) => {
    setTempState(prev => ({ ...prev, fullName: value }));
  };

  const handleEmailChange = (value: string) => {
    setTempState(prev => ({ ...prev, email: value }));
  };

  const applyFilters = () => {
    const newActiveFilters: string[] = [];
    
    if (tempState.accountName.trim() !== '') {
      newActiveFilters.push(`Account Name: ${tempState.accountName}`);
    }
    
    if (tempState.sfdcAccountId.trim() !== '') {
      newActiveFilters.push(`SFDC Account ID: ${tempState.sfdcAccountId}`);
    }
    
    if (tempState.fullName.trim() !== '') {
      newActiveFilters.push(`Full Name: ${tempState.fullName}`);
    }
    
    if (tempState.email.trim() !== '') {
      newActiveFilters.push(`Email: ${tempState.email}`);
    }
    
    setFilterState({
      accountName: tempState.accountName.trim(),
      sfdcAccountId: tempState.sfdcAccountId.trim(),
      fullName: tempState.fullName.trim(),
      email: tempState.email.trim(),
      activeFilters: newActiveFilters
    });
    
    setFilterDrawerOpen(false);
  };
  
  const clearFilters = () => {
    const emptyState = {
      accountName: '',
      sfdcAccountId: '',
      fullName: '',
      email: ''
    };
    
    setFilterState({
      ...emptyState,
      activeFilters: []
    });
    
    setTempState(emptyState);
  };

  const removeFilter = (filter: string) => {
    const newFilterState = { ...filterState };
    
    if (filter.startsWith('Account Name:')) {
      newFilterState.accountName = '';
      setTempState(prev => ({ ...prev, accountName: '' }));
    } else if (filter.startsWith('SFDC Account ID:')) {
      newFilterState.sfdcAccountId = '';
      setTempState(prev => ({ ...prev, sfdcAccountId: '' }));
    } else if (filter.startsWith('Full Name:')) {
      newFilterState.fullName = '';
      setTempState(prev => ({ ...prev, fullName: '' }));
    } else if (filter.startsWith('Email:')) {
      newFilterState.email = '';
      setTempState(prev => ({ ...prev, email: '' }));
    }
    
    newFilterState.activeFilters = newFilterState.activeFilters.filter(item => item !== filter);
    setFilterState(newFilterState);
  };
  
  useEffect(() => {
    setTempState({
      accountName: filterState.accountName,
      sfdcAccountId: filterState.sfdcAccountId,
      fullName: filterState.fullName,
      email: filterState.email
    });
  }, [filterState]);

  return {
    filterState,
    tempState,
    filterDrawerOpen,
    expandedAccordions,
    setFilterDrawerOpen,
    setExpandedAccordions,
    handleAccountNameChange,
    handleSfdcAccountIdChange,
    handleFullNameChange,
    handleEmailChange,
    applyFilters,
    clearFilters,
    removeFilter
  };
};

export default useStagingFilter;
